import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import type { PayeeCleanupSuggestion } from '#banksync/ai-rules';

type Props = {
  item: PayeeCleanupSuggestion;
  onAccept: () => void;
  onDismiss: () => void;
};

export function AiRuleSuggestionsCleanupRow({
  item,
  onAccept,
  onDismiss,
}: Props) {
  const { t } = useTranslation();
  const targetName = item.existingName ?? item.newName ?? '';

  // build variants preview inline so we can use t() for the "and N more" part
  const variantsSummary = (() => {
    const v = item.variants;
    if (v.length === 0) return '';
    if (v.length === 1) return v[0];
    const first = v.slice(0, 2).join(', ');
    const rest = v.length - 2;
    return rest > 0 ? t('{{first}} and {{rest}} more', { first, rest }) : first;
  })();
  const isNew = !item.existingName && !!item.newName;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        flexShrink: 0,
        borderBottom: '1px solid ' + theme.tableBorder,
        flexWrap: 'wrap',
      }}
    >
      {/* target payee name + tags */}
      <View
        style={{
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Text style={{ fontSize: 13, fontWeight: 600, color: theme.tableText }}>
          {targetName}
        </Text>
        {isNew && (
          <Text
            style={{
              fontSize: 10,
              color: theme.noticeTextLight,
              border: '1px solid ' + theme.noticeTextLight,
              borderRadius: 3,
              padding: '1px 4px',
            }}
          >
            {t('new')}
          </Text>
        )}
        {item.guess && (
          <Text
            style={{
              fontSize: 10,
              color: theme.pageTextSubdued,
              border: '1px solid ' + theme.tableBorder,
              borderRadius: 3,
              padding: '1px 4px',
            }}
          >
            {t('guess')}
          </Text>
        )}
      </View>

      {/* variants preview | full list in title for hover tooltip */}
      <Text
        title={item.variants.join(', ')}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          color: theme.pageTextSubdued,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {variantsSummary}
      </Text>

      <Button variant="bare" onPress={onAccept} style={{ flexShrink: 0 }}>
        <Trans>Accept</Trans>
      </Button>
      <Button
        variant="bare"
        onPress={onDismiss}
        style={{ flexShrink: 0, color: theme.pageTextSubdued }}
      >
        <Trans>Dismiss</Trans>
      </Button>
    </View>
  );
}
